import React, { useState, useEffect } from 'react';
import {
  Handshake,
  ArrowRight,
  Wallet,
  Landmark,
  X,
  Check
} from 'lucide-react';
import CustomAccountSelect from './CustomAccountSelect';

export default function GroupSettleModal({
  isOpen,
  activeGroup,
  groupMembers = [],
  accounts = [],
  prefill = null, // { from_user_id, to_user_id, amount, currency }
  currentUserId,
  onClose,
  onSubmit,
  formatMoney
}) {
  if (!isOpen) return null;

  const defaultCurrency = activeGroup?.currency || accounts[0]?.currency || 'AED';

  const [fromUserId, setFromUserId] = useState(prefill?.from_user_id || currentUserId || '');
  const [toUserId, setToUserId] = useState(prefill?.to_user_id || '');
  const [amount, setAmount] = useState(prefill?.amount ? String(prefill.amount) : '');
  const [currency, setCurrency] = useState(prefill?.currency || defaultCurrency);
  const [accountId, setAccountId] = useState(accounts[0]?.account_id || '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (prefill) {
      if (prefill.from_user_id) setFromUserId(prefill.from_user_id);
      if (prefill.to_user_id) setToUserId(prefill.to_user_id);
      if (prefill.amount) setAmount(String(prefill.amount));
      if (prefill.currency) setCurrency(prefill.currency);
    } else {
      setFromUserId(currentUserId || '');
      const otherMember = groupMembers.find(m => m.user_id !== currentUserId);
      if (otherMember) setToUserId(otherMember.user_id);
    }
  }, [prefill, currentUserId, groupMembers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromUserId || !toUserId || !amount || parseFloat(amount) <= 0) {
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        from_user_id: fromUserId,
        to_user_id: toUserId,
        amount: parseFloat(amount),
        currency: currency.toUpperCase(),
        account_id: accountId || null
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop modal-backdrop-blur">
      <form
        onSubmit={handleSubmit}
        className="glass-panel modal-spring-enter modal-content-panel"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          boxSizing: 'border-box',
          maxWidth: '440px',
          width: '100%'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #ff7a18 0%, #ff5238 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff'
            }}>
              <Handshake size={18} />
            </div>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>Settle Group Debt</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Payer -> Payee Selector */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(255, 255, 255, 0.03)',
          padding: '12px',
          borderRadius: '14px',
          border: '1px solid var(--border-glass)'
        }}>
          {/* Payer (Who is paying) */}
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: '600' }}>
              Payer (Pays)
            </label>
            <select
              value={fromUserId}
              onChange={e => setFromUserId(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid var(--border-glass)',
                color: 'var(--text-main)',
                fontSize: '13px',
                fontWeight: 'bold'
              }}
              required
            >
              {groupMembers.map(m => (
                <option key={m.user_id} value={m.user_id}>
                  {m.user_id === currentUserId ? 'You' : (m.display_name || m.nickname || m.email?.split('@')[0])}
                </option>
              ))}
            </select>
          </div>

          <ArrowRight size={18} color="var(--text-muted)" style={{ marginTop: '16px', flexShrink: 0 }} />

          {/* Payee (Who is receiving) */}
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', fontWeight: '600' }}>
              Recipient (Gets)
            </label>
            <select
              value={toUserId}
              onChange={e => setToUserId(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid var(--border-glass)',
                color: 'var(--text-main)',
                fontSize: '13px',
                fontWeight: 'bold'
              }}
              required
            >
              {groupMembers.map(m => (
                <option key={m.user_id} value={m.user_id} disabled={m.user_id === fromUserId}>
                  {m.user_id === currentUserId ? 'You' : (m.display_name || m.nickname || m.email?.split('@')[0])}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Amount & Currency */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: 2 }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
              Settlement Amount
            </label>
            <input
              type="number"
              step="any"
              min="0.01"
              placeholder="0.00"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid var(--border-glass)',
                color: 'var(--text-main)',
                fontSize: '16px',
                fontWeight: 'bold',
                boxSizing: 'border-box'
              }}
              required
            />
          </div>

          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
              Currency
            </label>
            <input
              type="text"
              value={currency}
              onChange={e => setCurrency(e.target.value.toUpperCase())}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid var(--border-glass)',
                color: 'var(--text-main)',
                fontSize: '14px',
                fontWeight: 'bold',
                textAlign: 'center',
                boxSizing: 'border-box'
              }}
              required
            />
          </div>
        </div>

        {/* Source Account (If current user is payer or payee) */}
        {(fromUserId === currentUserId || toUserId === currentUserId) && (
          <div>
            <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
              {fromUserId === currentUserId ? 'Pay From Account' : 'Deposit Into Account'}
            </label>
            <CustomAccountSelect
              value={accountId}
              onChange={e => setAccountId(e.target.value)}
              accounts={accounts}
              filter={a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts'))}
              placeholder={fromUserId === currentUserId ? 'Select Pay From Account' : 'Select Deposit Into Account'}
              formatMoney={formatMoney}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            style={{ flex: 1, padding: '12px', borderRadius: '12px', fontSize: '13px' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            style={{
              flex: 2,
              padding: '12px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #ff7a18 0%, #ff5238 100%)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(255, 82, 56, 0.3)'
            }}
          >
            {submitting ? 'Recording...' : '✓ Record Settlement'}
          </button>
        </div>
      </form>
    </div>
  );
}
